import { TranslatedString } from "@ct-react/core";
import { ArticleType, ArticleUrlData } from "@shared/url";
import { ExternalMedia, Image } from "../media";
import { Location } from "../common";
import { RentalAccommodationArticleDetails } from "./rental";
import { SeasonalAccommodationArticleDetails } from "./seasonal";
import { AnnualAccommodationArticleDetails } from "./annual";
import { SaleAccommodationArticleDetails } from "./sale";

type ArticleDetails = ArticleUrlData & {
  referenceId?: string;
  title: TranslatedString;
  description?: TranslatedString;
  images: Image[];
  documents: Document[];
  novelty: boolean;
  promoted: boolean;
  recommended: boolean;
  publishedAt?: string;
}

export type AccommodationArticleDetails = ArticleDetails & {
  address: Location;
  coordinates?: [ number, number ];
  locationDescription?: TranslatedString;
  plans?: Image[];
  virtualVisits?: ExternalMedia[];
}

export type HeatingSystem =
  "CONDENSING_BOILER" |
  "ELECTRIC" |
  "FIREPLACE" |
  "GAS_BOILER" |
  "HEAT_PUMP" |
  "HVAC" |
  "INSERT" |
  "OIL_BOILER" |
  "PELLET_BOILER" |
  "SOLAR_WATER_HEATER";

export type Orientation =
  "NONE" |
  "NORTH" |
  "NORTH_EAST" |
  "EAST" |
  "SOUTH_EAST" |
  "SOUTH" |
  "SOUTH_WEST" |
  "WEST" |
  "NORTH_WEST" |
  "PANORAMIC";

export type AnyAccommodationFeatures = {
  totalSpace?: number;
  livingSpace?: number;
  rooms?: number;
  bedrooms?: number;
  bathrooms?: number;
  restrooms?: number;
  parkings?: number;
  heatings?: HeatingSystem[];
  floor?: TranslatedString;
  orientation?: Orientation;
  withDishwasher?: boolean;
  withWashingMachine?: boolean;
  withLaundryRoom?: boolean;
  withFireplace?: boolean;
  withPool?: boolean;
  withSauna?: boolean;
  withLift?: boolean;
  withParking?: boolean;
  withGarage?: boolean;
  withBalcony?: boolean;
  withTerrace?: boolean;
  withOutdoorSpace?: boolean;
  withWIFI?: boolean;
  withTV?: boolean;
  onCityCenter?: boolean;
  onShoppingArea?: boolean;
  nearSkiArea?: boolean;
  nearBusStop?: boolean;
  withClearView?: boolean;
}

export type AnyArticleDetails =
  RentalAccommodationArticleDetails |
  SeasonalAccommodationArticleDetails |
  AnnualAccommodationArticleDetails |
  SaleAccommodationArticleDetails;

export const isRentalArticle = (val: AnyArticleDetails): val is RentalAccommodationArticleDetails =>
  val.__typename === ArticleType.RENTAL;

export const isSeasonalArticle = (val: AnyArticleDetails): val is SeasonalAccommodationArticleDetails =>
  val.__typename === ArticleType.SEASONAL;

export const isAnnualArticle = (val: AnyArticleDetails): val is AnnualAccommodationArticleDetails =>
  val.__typename === ArticleType.ANNUAL;

export const isSaleArticle = (val: AnyArticleDetails): val is SaleAccommodationArticleDetails =>
  val.__typename === ArticleType.SALE;
